export type FootstepEvent = 'left' | 'right' | null;

export interface FootstepTiming {
  walkIntervalMs: number;
  sprintIntervalMs: number;
}

/**
 * Pure stride-cadence state machine — deliberately has no Web Audio or DOM
 * dependency (same "formulas live in a plain function/class" convention as
 * `dungeon-prototype/sim/*.ts`) so it's unit-testable without a browser
 * environment. `AudioEngine.updateLocalFootsteps` is the only caller; it
 * decides WHEN a footstep sound should fire, not what the sound sounds like.
 */
export class FootstepTracker {
  private elapsedMs = 0;
  private nextFoot: 'left' | 'right' = 'left';
  private wasMoving = false;

  constructor(private readonly timing: FootstepTiming) {}

  /** Advances by dtSeconds given the current movement state; returns which foot just landed, or null if no step lands this tick. */
  advance(dtSeconds: number, isMoving: boolean, isSprinting: boolean): FootstepEvent {
    if (!isMoving) {
      this.wasMoving = false;
      this.elapsedMs = 0;
      return null;
    }
    // Resuming from a stop starts the cadence fresh instead of firing
    // instantly on whatever stale elapsedMs was left over from before the stop.
    if (!this.wasMoving) {
      this.wasMoving = true;
      this.elapsedMs = 0;
    }

    this.elapsedMs += dtSeconds * 1000;
    const interval = isSprinting ? this.timing.sprintIntervalMs : this.timing.walkIntervalMs;
    if (this.elapsedMs < interval) return null;

    this.elapsedMs -= interval; // keep the remainder for smooth cadence at variable framerate
    const foot = this.nextFoot;
    this.nextFoot = foot === 'left' ? 'right' : 'left';
    return foot;
  }
}
